import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MainContainer } from '../MainContainer';
import { getOffices, createOffice, updateOffice } from '../../actions/officeAction';
import { getAllCategory } from '../../actions/category.action';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { getNotificationId, getNotificationQueryId } from '../../utils/adminNotificationUtils';
import { Row, Col } from 'react-bootstrap';
import OfficeList from './OfficeList';
import OfficeFormModal from './OfficeFormModal';
import Loader from '../../components/Loader';
import { updateOfficeCreditLimit } from '../../api/adminPaymentsApi';

const OfficePage = () => {
    const [params, setParams] = useSearchParams();
    const notificationOfficeId = getNotificationQueryId(params.toString(), "notificationOfficeId");
    const notificationOpened = useRef("");
    const dispatch = useDispatch();
    const { offices, officeLoading, officeError } = useSelector((state) => state.office);
    const { user, authenticate } = useSelector((state) => state.auth);
    const [showModal, setShowModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [formItem, setFormItem] = useState({
        name: '',
        address: '',
        categories: [],
        accentColor: '#6366f1',
        coverImage: null,
        coverImageFile: null,
        removeCoverImage: false,
        watermarkMedia: null,
        watermarkImage: '',
        watermarkImageFile: null,
        removeWatermarkImage: false,
        creditLimit: 3000,
    }); // categoryIds → categories

    useEffect(() => {
        dispatch(getOffices());
    }, []);

    const handleOpenModal = useCallback((office = null) => {
        setCurrentId(office?._id || null);
        setFormItem({
            name: office?.name || '',
            address: office?.address || '',
            categories: office?.categories?.map((cat) => cat._id) || [], // categoryIds → categories
            accentColor: office?.accentColor || '#6366f1',
            coverImage: office?.coverImage || null,
            coverImageFile: null,
            removeCoverImage: false,
            watermarkMedia: office?.watermarkMedia || null,
            watermarkImage: office?.watermarkImage || '',
            watermarkImageFile: null,
            removeWatermarkImage: false,
            creditLimit: Number(office?.creditLimit ?? 3000),
        });
        setShowModal(true);
    }, []);

    useEffect(() => {
        if (!notificationOfficeId) {
            notificationOpened.current = "";
            return;
        }
        if (officeLoading || notificationOpened.current === notificationOfficeId) return;
        const office = (offices || []).find((item) => getNotificationId(item) === notificationOfficeId);
        if (!office) return;
        notificationOpened.current = notificationOfficeId;
        handleOpenModal(office);
    }, [notificationOfficeId, officeLoading, offices, handleOpenModal]);

    const handleCloseModal = () => {
        setShowModal(false);
        if (notificationOfficeId) {
            const next = new URLSearchParams(params);
            next.delete("notificationOfficeId");
            setParams(next, { replace: true });
        }
        setCurrentId(null);
        setFormItem({
            name: '',
            address: '',
            categories: [],
            accentColor: '#6366f1',
            coverImage: null,
            coverImageFile: null,
            removeCoverImage: false,
            watermarkMedia: null,
            watermarkImage: '',
            watermarkImageFile: null,
            removeWatermarkImage: false,
            creditLimit: 3000,
        });
    };

    const handleSubmit = async () => {
        try {
            if (currentId) {
                await dispatch(updateOffice(currentId, formItem));
                await updateOfficeCreditLimit(currentId, Number(formItem.creditLimit || 0));
            } else {
                await dispatch(createOffice(formItem));
            }
            dispatch(getOffices());
            handleCloseModal();
        } catch (error) {
            alert(error.payload?.error || 'Ошибка сохранения офиса');
        }
    };

    if (officeLoading) {
        return <Loader />;
    }

    return (
        <MainContainer type="add" title="Настройки офисов">
            {notificationOfficeId && !(offices || []).some((item) => getNotificationId(item) === notificationOfficeId) && (
                <div className="admin-notification-target" role="alert">
                    <span>Офис из уведомления не найден или недоступен.</span>
                    <Link to="/office-setting">Все офисы</Link>
                </div>
            )}
            <Row>
                <Col>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleOpenModal()}
                        style={{ marginBottom: '10px' }}
                    >
                        Добавить офис
                    </button>
                </Col>
            </Row>
            <OfficeList offices={offices} onEdit={handleOpenModal} officeError={officeError} />
            <OfficeFormModal
                show={showModal}
                handleClose={handleCloseModal}
                onSubmit={handleSubmit}
                currentId={currentId}
                formItem={formItem}
                setFormItem={setFormItem}
            />
        </MainContainer>
    );
};

export default OfficePage;
